import type { TFunction } from 'i18next';

/**
 * The key `failuresByReason` files a missing reason under: the server stringifies the reason, so a
 * NULL `failure_reason` arrives as the text "None".
 */
const NO_REASON_KEY = 'None';

/** httpx exception classes that all mean "the host took too long". */
const TIMEOUTS = new Set([
  'TimeoutException',
  'ConnectTimeout',
  'ReadTimeout',
  'WriteTimeout',
  'PoolTimeout',
]);

/** Integration's exception class → the plain-words locale key under `labels.imageFailure`. */
const CLASS_KEY: Record<string, string> = {
  ConnectError: 'unreachable',
  NetworkError: 'unreachable',
  RemoteProtocolError: 'unreachable',
  UnsupportedProtocol: 'notWebLink',
  InvalidURL: 'badLink',
  ValueError: 'badLink',
  DecodingError: 'corrupted',
  ImageDecodeError: 'notImage',
  ImageTooLargeError: 'tooLarge',
  OSError: 'unreadable',
};

/** "RemoteProtocolError" → "Remote protocol error": the last-resort label for an unknown class. */
function splitWords(code: string): string {
  const words = code
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_:]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** What one raw reason classifies as: the mapped label key `imageFailureLabel` renders, plus the
 *  HTTP status when the class is `HTTPStatusError` (the label text interpolates it). */
interface FailureClass {
  /** `labels.imageFailure.<key>`, or `class:<Name>` for an exception class nothing maps — its
   *  own distinct group, rendered via `splitWords` rather than a locale lookup. */
  key: string;
  status?: number;
}

function classifyImageFailure(reason: string | null | undefined): FailureClass {
  if (!reason || reason === NO_REASON_KEY) return { key: 'none' };
  const [name = '', status] = reason.split(':');
  const code = status ? Number(status) : NaN;
  if (name === 'HTTPStatusError' && Number.isFinite(code)) {
    const key =
      code === 404 || code === 410
        ? 'notFound'
        : code === 401 || code === 403
          ? 'blocked'
          : code === 429
            ? 'rateLimited'
            : code >= 500
              ? 'hostError'
              : 'http';
    return { key, status: code };
  }
  if (TIMEOUTS.has(name)) return { key: 'timeout' };
  const mapped = CLASS_KEY[name];
  return { key: mapped ?? `class:${name}` };
}

/**
 * An image failure reason in plain words. Integration records the exception class, plus the
 * HTTP status for a bad response (`HTTPStatusError:404`); the raw value belongs in a tooltip only.
 */
export function imageFailureLabel(
  t: TFunction<'catalog-enrichment-lineage-images'>,
  reason: string | null | undefined,
): string {
  const { key, status } = classifyImageFailure(reason);
  if (key === 'none') return t('labels.imageFailure.none');
  if (status !== undefined) return t(`labels.imageFailure.${key}`, { status });
  if (key.startsWith('class:')) return splitWords(key.slice('class:'.length));
  return t(`labels.imageFailure.${key}`);
}
